/**
 * /owner/membership/tiers — list + create + edit + soft-delete.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, EmptyState, FullSpinner, QueryError, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { Input, FieldRow, ColorField } from '@/components/ui_primitives/Field';
import TierBadge from '@/components/membership/TierBadge';

export default function OwnerMembershipTiersPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [editingTier, setEditingTier] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const tiersQ = useQuery({
    queryKey: qk.ownerMembershipTiers(brandId),
    queryFn: () => api.ownerMembershipTiers(token, brandId),
    enabled: !!token && !!brandId,
  });

  const deleteM = useMutation({
    mutationFn: (id) => api.ownerMembershipTierDelete(token, id),
    onSuccess: () => {
      toast.success('Tier archived');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipTiers(brandId) });
    },
    onError: (e) => toast.error(e?.detail || 'Could not delete tier'),
  });

  if (tiersQ.isLoading) return <FullSpinner />;
  if (tiersQ.isError) return <QueryError error={tiersQ.error} onRetry={() => tiersQ.refetch()} />;

  const tiers = [...(tiersQ.data ?? [])].sort(
    (a, b) => (b.priority_level || 0) - (a.priority_level || 0),
  );

  return (
    <div data-testid="owner-membership-tiers-page">
      <PageHeader
        kicker="Memberships"
        title="Tiers"
        subtitle="Order matters — higher priority tier outranks lower when displayed."
        actions={
          <Button
            variant="primary" size="md" leftIcon={<Plus size={14} />}
            onClick={() => { setEditingTier(null); setModalOpen(true); }}
            testID="tier-create-button"
          >
            New Tier
          </Button>
        }
      />

      <SectionCard padded={false} testID="tier-section-card">
        {tiers.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="No tiers yet"
              subtitle="Tiers describe the visual rank of a membership (Bronze → VIP). Create one to get started."
              testID="tier-empty"
            />
          </div>
        ) : (
          <Table testID="tiers-table">
            <THead>
              <Tr>
                <Th>Tier</Th>
                <Th>Color</Th>
                <Th align="center">Priority</Th>
                <Th align="center">Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {tiers.map((t) => (
                <Tr key={t.id} testID={`tier-row-${t.id}`}>
                  <Td><TierBadge tier={t} size="lg" /></Td>
                  <Td>
                    <span className="font-mono text-xs text-white/55">
                      {t.display_color}
                    </span>
                  </Td>
                  <Td align="center" className="font-mono">{t.priority_level ?? 0}</Td>
                  <Td align="center">
                    <StatusPill variant={t.active ? 'green' : 'slate'}>
                      {t.active ? 'Active' : 'Archived'}
                    </StatusPill>
                  </Td>
                  <Td align="right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        variant="ghost" size="sm" leftIcon={<Edit2 size={12} />}
                        onClick={() => { setEditingTier(t); setModalOpen(true); }}
                        testID={`tier-edit-${t.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger" size="sm" leftIcon={<Trash2 size={12} />}
                        loading={deleteM.isPending && deleteM.variables === t.id}
                        onClick={() => deleteM.mutate(t.id)}
                        testID={`tier-delete-${t.id}`}
                      >
                        Archive
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </SectionCard>

      <TierFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        brandId={brandId}
        editing={editingTier}
      />
    </div>
  );
}

function TierFormModal({ open, onClose, brandId, editing }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [name, setName] = useState(editing?.name ?? '');
  const [color, setColor] = useState(editing?.display_color ?? '#C9A84C');
  const [priority, setPriority] = useState(editing?.priority_level ?? 1);
  const [errors, setErrors] = useState({});

  // Reset on open
  useState(() => null); // dead init to keep linter happy when fields change
  // Use effect would be safer for non-stale, but the modal unmounts when closed
  // because of Modal's `if (!open) return null` — so this state init runs each open.

  const isEdit = !!editing;
  const m = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        display_color: color.trim() || '#C9A84C',
        priority_level: Number(priority) || 0,
      };
      return isEdit
        ? api.ownerMembershipTierPatch(token, editing.id, body)
        : api.ownerMembershipTierCreate(token, brandId, body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Tier updated' : 'Tier created');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipTiers(brandId) });
      onClose();
    },
    onError: (e) => {
      toast.error(e?.detail || 'Could not save tier');
      setErrors({ general: e?.detail || 'Save failed' });
    },
  });

  return (
    <Modal
      open={open} onClose={onClose}
      title={isEdit ? 'Edit Tier' : 'Create Tier'}
      subtitle="Tiers visually rank memberships. Higher priority wins."
      size="md"
      testID="tier-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={m.isPending}
            testID="tier-modal-cancel">Cancel</Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!name.trim()}
            onClick={() => m.mutate()}
            testID="tier-modal-save"
          >
            {isEdit ? 'Save changes' : 'Create tier'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <FieldRow label="Name" required>
          <Input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Platinum"
            data-testid="tier-modal-name"
          />
        </FieldRow>
        <FieldRow label="Display color" hint="Hex (e.g. #FFD700)">
          <ColorField value={color} onChange={(e) => setColor(e.target.value)}
            data-testid="tier-modal-color" />
        </FieldRow>
        <FieldRow label="Priority level" hint="Higher = ranks above other tiers">
          <Input
            type="number" min={0} value={priority}
            onChange={(e) => setPriority(e.target.value)}
            data-testid="tier-modal-priority"
          />
        </FieldRow>
        {errors.general ? (
          <div className="text-xs text-red-400">{errors.general}</div>
        ) : null}
      </div>
    </Modal>
  );
}
